import { useEffect, useState } from 'react';
import { client } from '../amplify-client';

export type FotoVariante = 'original' | 'web' | 'thumb';

/**
 * Löst ein Verkostungsfoto in eine kurzlebige, signierte URL auf.
 *
 * Security-Check-Fund (behoben — "Lese-Zusatzfund" aus der Sicherheitsprüfung):
 * Diese Funktion rief bis eben `getUrl()` aus `aws-amplify/storage` direkt
 * auf. Das nutzt die IAM-Zugangsdaten des Identity Pools — und
 * `storage/resource.ts` gab Leser/Tester bisher gruppenweiten Lesezugriff
 * auf den KOMPLETTEN `tests/*`-Präfix. Ergebnis: jeder Leser/Tester konnte
 * mit seinen eigenen, gültigen AWS-Zugangsdaten (z. B. per `list()` oder
 * direktem SDK-Aufruf, an der eigentlichen Oberfläche vorbei) auch
 * Entwurfs-Fotos fremder, noch unveröffentlichter Verkostungen lesen — die
 * Owner-Einschränkung existierte nur auf der GraphQL-/Datenebene, nicht für
 * den Datei-Zugriff selbst.
 *
 * Fix (spiegelbildlich zu Security-Review Fund 1 beim Hochladen): kein
 * gruppenbasierter S3-Lesezugriff mehr für Leser/Tester (siehe
 * storage/resource.ts). Stattdessen fragt diese Funktion die neue Query
 * `fotoUrlAnfordern` (amplify/functions/verkostung-schreiben) — die prüft
 * serverseitig Status (veröffentlicht → jeder) bzw. Eigentümer (Entwurf →
 * nur Ersteller/Verwalter), bevor sie eine auf GENAU dieses Foto gescopte,
 * kurzlebige Presigned-URL ausstellt.
 */
export function useFotoUrl(
  testId: string | null | undefined,
  fotoVorhanden: boolean | string | null | undefined,
  variante: FotoVariante = 'original',
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    setUrl(null);
    if (!testId || !fotoVorhanden) return;
    client.queries
      .fotoUrlAnfordern({ testId, variante })
      .then(({ data }) => {
        if (aktiv) setUrl(data ?? null);
      })
      .catch(() => {
        if (aktiv) setUrl(null);
      });
    return () => {
      aktiv = false;
    };
    // fotoVorhanden nur als Gate (üblicherweise das fotoKey-Feld selbst) —
    // sein Inhalt beeinflusst die Anfrage nicht, nur ob sie stattfindet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId, Boolean(fotoVorhanden), variante]);

  return url;
}

/**
 * Wie useFotoUrl, liefert aber bevorzugt die vom bild-varianten-Lambda
 * erzeugte Web-/Thumbnail-Variante (kleiner, WebP). Existiert sie
 * ausnahmsweise noch nicht — z. B. weil das Foto gerade erst hochgeladen
 * wurde und die Lambda noch läuft —, übernimmt der `onError`-Handler des
 * <img> im Aufrufer den Rückfall aufs Original (siehe `originalUrl`).
 */
export function useFotoVarianteUrl(
  testId: string | null | undefined,
  fotoVorhanden: boolean | string | null | undefined,
  variante: 'web' | 'thumb',
): { url: string | null; originalUrl: string | null } {
  return {
    url: useFotoUrl(testId, fotoVorhanden, variante),
    originalUrl: useFotoUrl(testId, fotoVorhanden, 'original'),
  };
}
